import { createApp } from "vue";
import { createPinia } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";
import "./assets/main.css";
import "./assets/tv-mode.css";
import "./assets/content-shelf.css";
import "./assets/filter-chip.css";
import "./assets/collection.css";
import "./assets/utility.css";
import "./assets/settings.css";
import "./assets/motion.css";
import App from "./App.vue";
import router from "./router";
import { useSettingsStore } from "./stores/settings";
import { fadeIn } from "./directives/fadeIn";

const app = createApp(App);

const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);

app.use(pinia);
app.use(router);

// Soft fade-in for poster / hero / logo images that don't already animate in
// (MediaCard + FeaturedHero handle their own). Opacity-only, so it's cheap.
app.directive("fade-in", fadeIn);

// Initialize theme after pinia is set up
const settings = useSettingsStore();
settings.initializeTheme();

app.mount("#app");

// Register the shell service worker only in production builds — in dev the
// SW would intercept Vite's HMR requests and serve stale modules.
if (import.meta.env.PROD && "serviceWorker" in navigator) {
  // Auto-pick-up new deploys without a manual (let alone HARD) refresh.
  //
  // The new SW calls skipWaiting() + clients.claim(), so when a deploy lands
  // it takes control of open tabs and fires `controllerchange`. We reload once
  // on that event to swap in the new HTML + asset bundles — but ONLY when:
  //   - the page was already controlled by an OLD worker (so we don't reload
  //     on the very first SW taking control of a fresh visit), and
  //   - we haven't already kicked off a reload (no reload loops).
  // If a video is actively playing we defer the reload until it pauses/ends so
  // we never yank someone out of a movie (the resume-position feature then
  // drops them right back where they were).
  const hadController = !!navigator.serviceWorker.controller;
  let reloading = false;
  const reloadForUpdate = () => {
    if (reloading) return;
    reloading = true;
    window.location.reload();
  };
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!hadController || reloading) return;
    const v = document.querySelector("video");
    const playing = v && !v.paused && !v.ended && v.readyState > 2;
    if (playing) {
      v.addEventListener("pause", reloadForUpdate, { once: true });
      v.addEventListener("ended", reloadForUpdate, { once: true });
      return;
    }
    reloadForUpdate();
  });

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js", { scope: "/" })
      .then((reg) => {
        // Proactively check for a new SW when the tab regains focus, so an
        // update lands shortly after a deploy instead of waiting for the next
        // full navigation.
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "visible")
            reg.update().catch(() => {});
        });
      })
      .catch((err) => {
        console.warn("SW registration failed:", err);
      });
  });
}
